import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Database, Target, Search, FileText, Brain, RefreshCw,
  Shield, Award, Lock, Globe, CheckCircle,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

// Features page — Swiss Minimalist + Pastel theme (mirrors Home).
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Single global font: Inter (.font-display / .font-body / .font-label all map to Inter)

const Features = () => {
  const navigate = useNavigate();

  const preparationFeatures = [
    { icon: Database,  title: 'Real Evidence Collection',    tag: 'Automation',  description: 'Automatically gather evidence from your cloud, code and HR systems — no manual screenshots or spreadsheets.' },
    { icon: Target,    title: 'Intelligent Control Mapping', tag: 'Automated',   description: 'Map your tech stack to the controls required across SOC 2, ISO 27001, PCI DSS, GDPR and HIPAA.' },
    { icon: Search,    title: 'Gap Analysis & Remediation',  tag: 'Actionable',  description: 'Spot gaps early and track remediation with a clear, prioritised roadmap.' },
    { icon: FileText,  title: 'Audit-Ready Documentation',   tag: 'Generated',   description: 'Generate policies and control documentation tailored to your environment, ready for review.' },
    { icon: RefreshCw, title: 'Continuous Monitoring',       tag: 'Always-On',   description: 'Stay audit-ready between cycles by monitoring for compliance drift and configuration changes.' },
  ];

  const frameworks = [
    { name: 'SOC 2 Type I & II', icon: Shield,      category: 'Certification' },
    { name: 'ISO 27001',         icon: Award,       category: 'Certification' },
    { name: 'PCI DSS',           icon: Lock,        category: 'Certification' },
    { name: 'GDPR',              icon: Globe,       category: 'Readiness' },
    { name: 'HIPAA',             icon: CheckCircle, category: 'Readiness' },
  ];

  const processSteps = [
    { num: '01', title: 'Assess Your Readiness' },
    { num: '02', title: 'Connect Your Systems' },
    { num: '03', title: 'Prepare for Audit' },
    { num: '04', title: 'Independent Auditor Review' },
  ];

  const tints = ['#D4E0DA', '#EBD8C8', '#D9CFD9'];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="features-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            AI-Powered Compliance and Certification
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            Everything you need for
            <br />
            compliance and certification
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-2xl mx-auto leading-relaxed mb-10">
            Automate evidence collection, control mapping and documentation — everything you need to prepare for independent certification.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="features-get-started"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-300"
            >
              Start Free Trial
            </button>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              data-testid="features-view-pricing"
              className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* ===================== PREPARATION FEATURES (BENTO BORDER GRID) ===================== */}
      <section className="px-6 py-24 md:py-32" data-testid="features-preparation">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
              Audit Preparation
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">
              How Nova prepares you for audit
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              Features that handle the heavy lifting of compliance preparation — so your team can focus on the business.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10 dark:border-white/10">
            {preparationFeatures.map((feature, i) => {
              const Icon = feature.icon;
              const tint = tints[i % tints.length];
              return (
                <div
                  key={feature.title}
                  data-testid={`feature-card-${i}`}
                  className="group relative border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700 flex flex-col"
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = tint + '40'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <div className="flex items-start justify-between mb-8">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center nv-tint"
                      style={{ backgroundColor: tint + 'AA' }}
                    >
                      <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                    </div>
                    <span className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55">
                      {feature.tag}
                    </span>
                  </div>
                  <p
                    className="font-display font-semibold leading-none text-5xl md:text-6xl mb-6 select-none"
                    style={{ color: tint }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-display font-semibold text-xl md:text-2xl leading-tight mb-3">
                    {feature.title}
                  </h3>
                  <p className="font-body text-base text-black/65 dark:text-white/65 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS (process) ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="features-how-it-works">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight text-center mb-20">
            How it works
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {processSteps.map((step) => (
              <div
                key={step.num}
                data-testid={`process-step-${step.num}`}
                className="border-t border-black/10 dark:border-white/10 pt-6"
              >
                <span className="block font-display font-normal text-5xl md:text-6xl text-black/25 dark:text-white/35 leading-none mb-5 select-none">
                  {step.num}
                </span>
                <span className="block font-display font-semibold text-lg md:text-xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-snug">
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FRAMEWORKS ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="features-frameworks">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
              Coverage
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">
              Frameworks we support
            </h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              One platform to get audit-ready across every major compliance framework. Certification is always issued by independent auditors.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 border-t border-l border-black/10 dark:border-white/10">
            {frameworks.map((framework, i) => {
              const Icon = framework.icon;
              const tint = tints[i % tints.length];
              const isCert = framework.category === 'Certification';
              return (
                <div
                  key={framework.name}
                  data-testid={`framework-${i}`}
                  className="group border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700 flex items-center gap-5"
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = tint + '35'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 nv-tint"
                    style={{ backgroundColor: tint + 'AA' }}
                  >
                    <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-lg md:text-xl text-[#0A0A0A] dark:text-[#F5F5F5] leading-tight">
                      {framework.name}
                    </h3>
                    <p className={`font-label text-[10px] uppercase tracking-[0.22em] mt-1 ${isCert ? 'text-black/65 dark:text-white/65' : 'text-black/45 dark:text-white/50'}`}>
                      {framework.category}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section
        className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center"
        data-testid="features-cta"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-10 leading-[1.1] tracking-tight">
            Start Your Compliance and Certification Journey
          </h2>
          <p className="font-body text-base md:text-lg text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
            Begin with a free 60-minute assessment to understand your compliance gaps and prepare for independent certification.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/register')}
              data-testid="features-cta-start"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] uppercase tracking-[0.2em] text-xs px-8 py-4 h-auto rounded-full shadow-sm hover:shadow-md transition-all duration-500"
            >
              Start Free Trial
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/pricing')}
              data-testid="features-cta-pricing"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 h-auto rounded-full transition-all duration-500"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
